// This file contains type definitions for the Pulse feature in the GitHub UI.

// The Pulse OverviewDataPoint type represents the data structure for overview statistics.
// It is used to display various metrics related to repository activity over a specified period.
export type PulseOverviewDataPoint = {
  period: string
  rangeLabel: string
  totalPullRequests: number
  totalIssues: number
  mergedPulls: PullRequestDataPoint[]
  numAuthorsMergedPulls?: number
  newPulls: PullRequestDataPoint[]
  numAuthorsNewPulls?: number
  closedIssues: IssueDataPoint[]
  numAuthorsClosedIssues?: number
  newIssues: IssueDataPoint[]
  numAuthorsNewIssues?: number
  discussions: InfographicDataPoint[]
  numAuthorsDiscussions?: number
  releases: InfographicDataPoint[]
  numAuthorsReleases?: number
}

// Default empty overview data
export const defaultOverviewData: PulseOverviewDataPoint = {
  period: '',
  rangeLabel: '',
  totalPullRequests: -1,
  totalIssues: -1,
  mergedPulls: [],
  numAuthorsMergedPulls: -1,
  newPulls: [],
  numAuthorsNewPulls: -1,
  closedIssues: [],
  numAuthorsClosedIssues: -1,
  newIssues: [],
  numAuthorsNewIssues: -1,
  discussions: [],
  numAuthorsDiscussions: -1,
  releases: [],
  numAuthorsReleases: -1,
}

// The InfographicDataPoint type represents the data structure for individual pull requests,
// issues, unresolved conversations, and releases.
// It includes details such as title, creation date, number, and URL path.
export type InfographicDataPoint = {
  title: string
  createdAt: string
  mergedAt?: string
  closedAt?: string
  number: number
  path: string
  commentsCount?: number // to accommodate active discussions data
}

export type PullRequestDataPoint = InfographicDataPoint & {
  closedAt?: never
}

export type IssueDataPoint = InfographicDataPoint & {
  mergedAt?: never
}

// The PulseAuthorCommitsDataPoint type represents the data structure for commit author information.
// It includes details about the author's commits, profile, and hovercard URL.
export type PulseAuthorCommitsDataPoint = {
  name: string | null
  login: string | null
  gravatar: string | null
  commits: number
  hovercardUrl: string | null
}

// The PulseDiffstatSummary type represents the summary of diffstat data for a repository.
// It's primary usage is to populate the "Summary" section of the Pulse page.
export type PulseDiffstatSummary = {
  authorsWithCommits: number
  filesChanged: number
  additions: number
  deletions: number
  totalCommits: number
  masterCommits: number
  defaultBranch: string
  comparePathUrl: string
  forkPathUrl?: string
}

export const defaultDiffstatSummary: PulseDiffstatSummary = {
  authorsWithCommits: -1,
  filesChanged: -1,
  additions: -1,
  deletions: -1,
  totalCommits: -1,
  masterCommits: -1,
  defaultBranch: '',
  comparePathUrl: '',
}

// The Period type defines the available time periods for Pulse data.
export const Period = {
  daily: '24 hours',
  halfweekly: '3 days',
  weekly: '1 week',
  monthly: '1 month',
} as const
export type Period = (typeof Period)[keyof typeof Period]

export const PeriodMap: Record<Period, keyof typeof Period> = {
  [Period.daily]: 'daily',
  [Period.halfweekly]: 'halfweekly',
  [Period.weekly]: 'weekly',
  [Period.monthly]: 'monthly',
} as const
export type PeriodMap = keyof typeof Period
